import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { formatDistanceToNow } from "date-fns";
import type React from "react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import type { IssueOccurrence } from "../types";
import { clickableProps } from "../utils/keyboard";
import { DataGrid } from "./data-grid";

interface IssueOccurrencesTableProps {
  data: IssueOccurrence[];
  isLoading?: boolean;
  onView?: (occurrence: IssueOccurrence) => void;
  height?: number | string;
}

export const IssueOccurrencesTable: React.FC<IssueOccurrencesTableProps> = ({
  data,
  isLoading = false,
  onView,
  height = 500,
}) => {
  const nav = useNavigate();

  const handleViewClick = useCallback(
    (occurrence: IssueOccurrence) => {
      if (onView) {
        onView(occurrence);
      }
    },
    [onView],
  );

  const columns = useMemo<GridColDef<IssueOccurrence>[]>(() => {
    const cols: GridColDef<IssueOccurrence>[] = [
      {
        field: "description",
        headerName: "Description",
        flex: 1,
        minWidth: 300,
      },
      {
        field: "test_run_id",
        headerName: "Test Run",
        width: 200,
        renderCell: (params: GridRenderCellParams<IssueOccurrence>) => {
          if (!params?.value) return "-";
          return (
            <Tooltip title={params.value}>
              <Typography
                variant="caption"
                noWrap
                width="100%"
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                sx={{ fontFamily: "monospace", cursor: "pointer" }}
                {...clickableProps(() => nav(`/runs/${params.value}`))}
                onClick={() => nav(`/runs/${params.value}`)}
              >
                {params.value.split("-")[0]}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "test_suite_run_id",
        headerName: "Test Suite Run",
        width: 200,
        renderCell: (params: GridRenderCellParams<IssueOccurrence>) => {
          if (!params?.value) return "-";
          return (
            <Tooltip title={params.value}>
              <Typography
                variant="caption"
                noWrap
                width="100%"
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                sx={{ fontFamily: "monospace", cursor: "pointer" }}
                {...clickableProps(() => nav(`/suite-runs/${params.value}`))}
                onClick={() => nav(`/suite-runs/${params.value}`)}
              >
                {params.value.split("-")[0]}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "discovery_run_id",
        headerName: "Discovery Run",
        width: 200,
        renderCell: (params: GridRenderCellParams<IssueOccurrence>) => {
          if (!params?.value) return "-";
          return (
            <Tooltip title={params.value}>
              <Typography
                variant="caption"
                noWrap
                width="100%"
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                sx={{ fontFamily: "monospace", cursor: "pointer" }}
                {...clickableProps(() => nav(`/discovery-runs/${params.value}`))}
                onClick={() => nav(`/discovery-runs/${params.value}`)}
              >
                {params.value.split("-")[0]}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "created_at",
        headerName: "Created",
        width: 200,
        valueGetter: (params) => {
          if (!params) return "-";
          return formatDistanceToNow(new Date(params), { addSuffix: true });
        },
      },
    ];

    if (onView) {
      cols.push({
        field: "actions",
        headerName: "Actions",
        width: 80,
        sortable: false,
        renderCell: (params: GridRenderCellParams<IssueOccurrence>) => (
          <Stack direction="row" alignItems="center" justifyContent="center" height="100%">
            <Tooltip title="View Issue">
              <IconButton
                aria-label="View Issue"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewClick(params.row);
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      });
    }

    return cols;
  }, [onView, handleViewClick, nav]);

  return (
    <Box>
      <DataGrid
        rows={data}
        columns={columns}
        loading={isLoading}
        customSx={{
          height: height,
        }}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
      />
    </Box>
  );
};
